import React, { Component, ComponentType } from 'react'
import { Autocomplete, Chip, IconButton, styled, TextField, TextFieldProps, Tooltip, Typography } from '@mui/material'
import RemoveIcon from '@mui/icons-material/Remove'
import { IFormInputBase } from './types'
import { getErrorMessage } from './helpers'

export interface IAutocompleteChipSlots {
  optionItems?: string[]
  limitTags?: number
  separator?: string
  saveLocalStorageByKey?: string
}

export interface IAutocompleteChipProps<T> extends IFormInputBase<T, IAutocompleteChipSlots> {}

export interface IAutocompleteChipState {
  value: string[]
  options: string[]
}

function CreateAutocompleteChip<T>(params?: IAutocompleteChipSlots): ComponentType<IAutocompleteChipProps<T>> {
  const separator = params?.separator || '|'
  const limitTags = params?.limitTags || 2
  const storeItems = params?.saveLocalStorageByKey ? AutocompleteChipStore.initial(params.saveLocalStorageByKey) : undefined

  class AutocompleteChip extends Component<IAutocompleteChipProps<T>, IAutocompleteChipState> {
    constructor(props: IAutocompleteChipProps<T>) {
      super(props)
      this.state = {
        value: this.getDefaultValue(),
        options: this.getSelectOptions()
      }
    }

    refInput: HTMLInputElement | null = null
    render() {
      const { name } = this.props
      return (
        <>
          <Autocomplete
            multiple
            freeSolo
            limitTags={limitTags}
            value={this.state.value}
            onChange={this.handleAddOption}
            options={this.state.options}
            renderOption={(props, option) => (
              <ItemOption {...props} key={option}>
                <Typography variant='subtitle1' sx={{ flex: 1 }}>
                  {option}
                </Typography>
                {this.isBtnRemoveOption(option) && (
                  <Tooltip title='Remove option' arrow placement='left'>
                    <IconButton size='small' onClick={(e) => this.handleRemoveItem(e, option)}>
                      <RemoveIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
              </ItemOption>
            )}
            renderTags={(tagValue, getTagProps) => {
              return tagValue.map((option, index) => <Chip label={option} {...getTagProps({ index })} key={option.toString()} />)
            }}
            renderInput={(params) => <TextField {...params} variant='outlined' {...this.getTextFieldProps()} />}
          />
          <input
            ref={(ref) => {
              this.refInput = ref
            }}
            hidden
            type='text'
            name={name?.toString()}
            defaultValue={this.state.value.join(separator)}
          />
        </>
      )
    }

    handleRemoveItem = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, value: string) => {
      e.preventDefault()
      e.stopPropagation()
      const list = storeItems?.delete(value) ?? []
      this.setState({ options: list })
    }

    handleAddOption = (_: React.SyntheticEvent, newValue: string[]) => {
      if (params?.saveLocalStorageByKey) {
        const options = storeItems?.setItems(this.state.options, newValue) ?? this.state.options
        this.setState({ value: newValue, options })
      } else {
        this.setState({ value: newValue })
      }
      if (this.refInput) {
        this.refInput.value = newValue.join(separator)
      }
      this.props.name && this.props.onBlur && this.props.onBlur(this.props.name)
    }

    isBtnRemoveOption = (value: string) => {
      const isSelectedOption = this.state.value.findIndex((x) => x === value) < 0
      const selectOptions = params?.optionItems ?? this.props.slots?.optionItems ?? []
      const isDefault = selectOptions.findIndex((x) => x === value) < 0
      return isDefault && isSelectedOption
    }

    getSelectOptions = () => {
      let items = params?.optionItems ?? []
      if (params?.saveLocalStorageByKey) {
        items = storeItems?.getItemsAndMerge(items) ?? []
      }
      return items
    }

    getTextFieldProps = (): TextFieldProps => {
      const { name, label, placeholder, onBlur, messageErrors } = this.props
      const eMessage = getErrorMessage(messageErrors, name)
      return {
        label,
        placeholder: placeholder || 'Add new',
        onBlur: () => {
          if (!name) return
          onBlur && onBlur(name)
        },
        error: eMessage.error,
        helperText: eMessage.message
      }
    }

    getDefaultValue = (): string[] => {
      const { name, data } = this.props
      const value = name ? data?.[name]?.toString() : ''
      return value?.split(separator).filter((x) => !!x) ?? []
    }
  }

  return AutocompleteChip
}

export default CreateAutocompleteChip

const ItemOption = styled('li')({
  display: 'flex',
  alignItems: 'center'
})

type TChipStoreRoot = { [key: string]: string[] }

class AutocompleteChipStore {
  private storeKeyRoot = 'autocomplete_chip_store'
  private storeKey: string
  constructor(key: string, defaultValue?: string[]) {
    this.storeKey = key
    if (defaultValue) {
      this.setChild(defaultValue)
    }
  }

  static initial = (key: string, defaultValue?: string[]) => new AutocompleteChipStore(key, defaultValue)

  private getRoot = (): TChipStoreRoot => {
    try {
      const res = window.localStorage.getItem(this.storeKeyRoot)
      return JSON.parse(res ?? '{}')
    } catch (error) {
      return {}
    }
  }

  private getChild = (): string[] => {
    return this.getRoot()[this.storeKey]
  }

  private setRoot = (value: TChipStoreRoot) => {
    window.localStorage.setItem(this.storeKeyRoot, JSON.stringify(value))
  }

  private setChild = (value: string[]) => {
    const obj = this.getRoot()
    obj[this.storeKey] = value
    this.setRoot(obj)
  }

  getItemsAndMerge = (value: string[]): string[] => {
    try {
      return Array.from(new Set([...this.getChild(), ...value])).sort()
    } catch (error) {
      return value
    }
  }

  setItems = (options: string[], value: string[]): string[] => {
    const list = Array.from(new Set([...options, ...value])).sort()
    this.setChild(list)
    return list
  }

  delete = (value: string) => {
    const list = this.getChild().filter((x) => x !== value)
    this.setChild(list)
    return list
  }
}
